/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import Badge from 'bundles/unified-description-page-common/components/shared/badges/Badge';
import { shouldShowGoogleRefresh } from 'bundles/unified-description-page-common/utils/shouldShowGoogleRefresh';

import _t from 'i18n!nls/xdp';

const styles = {
  svg: css`
    margin-bottom: -2px;
  `,
  button: css`
    padding: 0;
    vertical-align: baseline;
  `,
  subtitle: css`
    line-height: 1.5;
    margin-bottom: var(--cds-spacing-300);
  `,
  list: css`
    padding-left: 1.5em;

    li {
      list-style: disc;
    }
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

type Props = {
  slug: string | undefined;
};

const NewAiBadge = ({ slug }: Props) => {
  const track = useTracker();
  const [isOpenned, setIsOpenned] = useState(false);

  const handleClick = () => {
    setIsOpenned(true);

    track('click_button', {
      button: {
        name: 'xdp_gen_ai_modal',
      },
    });
  };

  const handleClose = () => {
    setIsOpenned(false);
  };

  const shouldRenderShowGoogleRefresh = shouldShowGoogleRefresh(slug);

  const getSubheadingProfession = () => {
    if (slug === 'google-data-analytics') {
      return _t('data analytics');
    }
    if (slug === 'google-digital-marketing-ecommerce') {
      return _t('digital marketing & e-commerce');
    }
    if (slug === 'google-it-support') {
      return _t('IT support');
    }
    if (slug === 'google-project-management') {
      return _t('project management');
    }
    if (slug === 'google-ux-design') {
      return _t('UX design');
    }
    if (slug === 'google-cybersecurity') {
      return _t('cybersecurity');
    }

    return null;
  };

  const getContent = () => {
    if (slug === 'google-data-analytics') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your data analytics skills with AI')}</li>
          <li>{_t('Clean and prepare data with help from AI')}</li>
          <li>{_t('Organize data and build formulas using AI')}</li>
          <li>{_t('Use AI to help you ask more effective questions')}</li>
          <li>{_t('Create engaging data visualizations with AI')}</li>
          <li>{_t('Improve your R code with help from AI')}</li>
        </ul>
      );
    }
    if (slug === 'google-digital-marketing-ecommerce') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your digital marketing skills with AI')}</li>
          <li>{_t('Use AI to help you understand your audience')}</li>
          <li>{_t('Kickstart marketing strategy ideas with AI')}</li>
          <li>{_t('Use AI to compare two campaign proposals')}</li>
          <li>{_t('Improve your email marketing using AI')}</li>
          <li>{_t('Brainstorm website copy ideas with AI')}</li>
        </ul>
      );
    }
    if (slug === 'google-it-support') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your IT support skills with AI')}</li>
          <li>{_t('Leverage AI for faster troubleshooting')}</li>
          <li>{_t('Communicate technical concepts with AI')}</li>
          <li>{_t('Automate routine IT tasks with AI')}</li>
          <li>{_t('Simplify IT documentation using AI')}</li>
          <li>{_t('Use AI to identify IT security risks')}</li>
        </ul>
      );
    }
    if (slug === 'google-project-management') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your project management skills with AI')}</li>
          <li>{_t('Use AI to create a project charter')}</li>
          <li>{_t('Identify potential project risks with gen AI')}</li>
          <li>{_t('Use AI to improve project communications')}</li>
          <li>{_t('Execute meetings more efficiently with AI')}</li>
          <li>{_t('Plan Sprint retrospectives using AI')}</li>
        </ul>
      );
    }
    if (slug === 'google-ux-design') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your UX design skills with AI')}</li>
          <li>{_t('Generate personas with help from AI')}</li>
          <li>{_t('Use AI to help with design iteration')}</li>
          <li>{_t('Improve stakeholder communication with AI')}</li>
          <li>{_t('Simplify complex frameworks using AI')}</li>
          <li>{_t('Use AI to help write your portfolio copy')}</li>
        </ul>
      );
    }
    if (slug === 'google-cybersecurity') {
      return (
        <ul css={styles.list}>
          <li>{_t('Boost your cybersecurity skills with AI')}</li>
          <li>{_t('Decode complex security frameworks with AI')}</li>
          <li>{_t('Use AI to help identify bugs')}</li>
          <li>{_t('Refine code with help from AI')}</li>
          <li>{_t('Understand system vulnerabilities with AI')}</li>
          <li>{_t('Prioritize alerts with AI')}</li>
        </ul>
      );
    }

    return null;
  };

  if (!shouldRenderShowGoogleRefresh) {
    return null;
  }

  return (
    <>
      <Button size="small" variant="ghost" css={styles.button} onClick={handleClick}>
        <Badge>
          <Typography2 variant="inherit" color="inherit" component="p">
            <AIGenerateIcon size="small" css={styles.svg} />
            {_t('New AI skills')}
          </Typography2>
        </Badge>
      </Button>
      <Dialog open={isOpenned} onClose={handleClose}>
        <HeadingGroup>{_t('New AI skills')}</HeadingGroup>
        <Content>
          <Typography2 variant="subtitleMedium" component="p" css={styles.subtitle}>
            <FormattedMessage
              message={_t('This Professional Certificate includes 6 new videos on how to use AI in {profession}.')}
              profession={getSubheadingProfession()}
            />
          </Typography2>
          <Typography2 variant="bodyPrimary" component="span">
            {getContent()}
          </Typography2>
        </Content>
        <Actions />
      </Dialog>
    </>
  );
};

export default NewAiBadge;
